import { useEffect } from 'react'

import { performTokenRefresh } from '@/lib/auth-token-refresh'
import { getJwtExpiryMs } from '@/lib/jwt-utils'
import { useAuthStore } from '@/stores/authStore'

/** Refresh this long before access token `exp` so long forms rarely hit 401. */
const REFRESH_BEFORE_EXPIRY_MS = 90_000

/** When returning to the tab, refresh if the access token expires within this window. */
const VISIBILITY_REFRESH_IF_EXPIRES_WITHIN_MS = 120_000

/**
 * Proactively refreshes the JWT access token before it expires so users stay signed in
 * during long single-page work (few API calls). Mount once under the app shell.
 */
export function SessionRefreshScheduler() {
  const accessToken = useAuthStore(s => s.accessToken)
  const refreshToken = useAuthStore(s => s.refreshToken)
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !refreshToken) {
      return
    }

    const timers: number[] = []

    const scheduleRefresh = () => {
      const expMs = getJwtExpiryMs(accessToken)
      if (!expMs) {
        return
      }
      const now = Date.now()
      const delay = Math.max(0, expMs - now - REFRESH_BEFORE_EXPIRY_MS)
      const id = window.setTimeout(() => {
        void performTokenRefresh().catch(() => {
          // Interceptor handles hard failure on the next API call; silent here avoids duplicate toasts.
        })
      }, delay)
      timers.push(id)
    }

    scheduleRefresh()

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      const token = useAuthStore.getState().accessToken
      if (!token) {
        return
      }
      const expMs = getJwtExpiryMs(token)
      if (!expMs) {
        return
      }
      if (expMs - Date.now() < VISIBILITY_REFRESH_IF_EXPIRES_WITHIN_MS) {
        void performTokenRefresh().catch(() => {})
      }
    }

    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      for (const id of timers) {
        window.clearTimeout(id)
      }
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [accessToken, refreshToken, isAuthenticated])

  return null
}
